import React, { useEffect } from 'react';
import { createInertiaApp, router } from '@inertiajs/react';
import { createRoot } from 'react-dom/client';
import { ToastProvider, useToast } from '../design-system/components/Toast';
import { initializeTheme, applyCssVars } from '../design-system/hooks/useTheme';

// Apply theme class and color CSS vars before React renders to prevent flash
const appEl = document.getElementById('app');
const initialPage = appEl ? JSON.parse(appEl.dataset.page) : {};
initializeTheme(initialPage.props?.theme_css_vars, initialPage.props?.theme_dark_only);

// Re-apply color CSS vars after Inertia SPA navigation (e.g., after theme switch redirect)
router.on('navigate', (event) => {
  const { theme_css_vars, theme_dark_only } = event.detail.page.props || {};
  if (theme_css_vars) applyCssVars(theme_css_vars, theme_dark_only);
});

// Global ref for the toast function so router event handlers can use it
let globalAddToast = null;

// Listen for Inertia exceptions (network errors, timeouts)
router.on('exception', (event) => {
  event.preventDefault();
  globalAddToast?.({
    variant: 'error',
    message: 'A network error occurred. Please check your connection and try again.',
    duration: 8000,
  });
});

// Listen for invalid Inertia responses (e.g., server returned non-Inertia HTML)
router.on('invalid', (event) => {
  event.preventDefault();
  globalAddToast?.({
    variant: 'error',
    message: 'The server returned an unexpected response. Please try again.',
    duration: 8000,
  });
});

// Bridge component that captures the toast function from React context
function GlobalErrorHandler({ children }) {
  const { addToast } = useToast();

  useEffect(() => {
    globalAddToast = addToast;
    return () => {
      globalAddToast = null;
    };
  }, [addToast]);

  return <>{children}</>;
}

createInertiaApp({
  progress: {
    color: '#2563eb',
    showSpinner: true,
  },
  resolve: (name) => {
    const pages = import.meta.glob('../pages/**/*.jsx', { eager: true });
    const page = pages[`../pages/${name}.jsx`];
    if (!page) {
      throw new Error(`Page not found: ${name}`);
    }
    return page;
  },
  setup({ el, App, props }) {
    createRoot(el).render(
      <ToastProvider>
        <GlobalErrorHandler>
          <App {...props} />
        </GlobalErrorHandler>
      </ToastProvider>,
    );
  },
});
